/**
 * Royalty rules — the bit that makes this not-quite-chess.
 *
 * Each side has a King and a Prince. While a player's Prince is still on the
 * board, the King is wholly immune: it can never be checked, mated, or captured
 * — the Prince shields it. You must capture the enemy Prince first; only then do
 * ordinary check/checkmate rules apply to their King (win by checkmate, never by
 * actually capturing the King).
 */
import { pieceDef } from "./pieces";
import type { GameState, PieceTypeId, PlayerId, Square } from "./types";

/** Locate a piece by owner and type, or null. */
export function findPiece(
  state: GameState,
  owner: PlayerId,
  type: PieceTypeId,
): Square | null {
  for (let sq = 0; sq < state.board.length; sq++) {
    const p = state.board[sq];
    if (p && p.owner === owner && p.type === type) return sq;
  }
  return null;
}

export function isPrinceAlive(state: GameState, owner: PlayerId): boolean {
  return findPiece(state, owner, "prince") !== null;
}

export function isKingAlive(state: GameState, owner: PlayerId): boolean {
  return findPiece(state, owner, "king") !== null;
}

/**
 * Whether a player's King is vulnerable at all — i.e. their Prince has fallen.
 * While the Prince stands the King is immune (no check, mate, or capture), so
 * this gates when check/checkmate logic becomes meaningful for that side.
 */
export function isKingCapturable(state: GameState, kingOwner: PlayerId): boolean {
  return !isPrinceAlive(state, kingOwner);
}

/** The square a player's King sits on, or null if it has left the board. */
export function kingSquare(state: GameState, owner: PlayerId): Square | null {
  return findPiece(state, owner, "king");
}

/**
 * Whether a piece sitting on `target` may legally be captured by `attacker`.
 * Any enemy piece may be taken, with two exceptions: terrain (the Hill) is never
 * capturable, and a King is untouchable while its own Prince still stands.
 */
export function canCapture(
  state: GameState,
  attacker: PlayerId,
  target: Square,
): boolean {
  const victim = state.board[target];
  if (!victim || victim.owner === attacker) return false;
  // Some pieces (the Hill) are terrain and can never be captured by anyone.
  if (pieceDef(victim.type).capturable === false) return false;
  // A guarded King (its Prince still alive) cannot be captured.
  if (pieceDef(victim.type).royal === "king" && isPrinceAlive(state, victim.owner)) return false;
  return true;
}

/**
 * Is `target` attacked by any piece belonging to `byPlayer`?
 *
 * This uses the raw geometric movement of each piece (not the higher-level
 * legal-move filter) so it stays free of self-check recursion. A piece attacks
 * `target` when its movement yields a capturing move onto it — which, for the
 * check test, is evaluated against a board where the King occupies that square.
 */
export function isSquareAttacked(
  state: GameState,
  target: Square,
  byPlayer: PlayerId,
): boolean {
  for (let from = 0; from < state.board.length; from++) {
    const piece = state.board[from];
    if (!piece || piece.owner !== byPlayer) continue;
    const raw = pieceDef(piece.type).movement({ state, from, piece });
    for (const m of raw) {
      if (m.capture && m.to === target) return true;
    }
  }
  return false;
}

/**
 * Is `owner`'s King currently in check? A King guarded by a living Prince is
 * immune, so it is never considered in check while the Prince stands.
 */
export function isInCheck(state: GameState, owner: PlayerId): boolean {
  if (isPrinceAlive(state, owner)) return false;
  const king = kingSquare(state, owner);
  if (king === null) return false;
  return isSquareAttacked(state, king, otherPlayer(owner));
}

/**
 * Is `owner`'s Prince under attack — i.e. an enemy piece could capture it next?
 * The Prince has no special protection, so a threatened Prince is the warning
 * sign that the King is about to lose its shield. False once the Prince has
 * already fallen. (Unlike the King, the Prince is never "immune", so this holds
 * regardless of whether the Prince is alive on the other side.)
 */
export function isPrinceThreatened(state: GameState, owner: PlayerId): boolean {
  const prince = findPiece(state, owner, "prince");
  if (prince === null) return false;
  return isSquareAttacked(state, prince, otherPlayer(owner));
}

export const otherPlayer = (p: PlayerId): PlayerId => (p === 0 ? 1 : 0);
